import { useEffect, useMemo, useState } from "react";
import {
    Search,
    RefreshCw,
    ShieldOff,
    Shield,
    Trash2,
    Phone,
    Calendar,
    Users as UsersIcon,
    ChevronLeft,
    ChevronRight,
    Loader2,
    AlertTriangle,
    Wallet,
    Coins,
    Activity,
    Headset,
    Star,
    X,
} from "lucide-react";
import API from "../api/api";
import BatchActionBar from "../components/BatchActionBar";

const statusTabs = [
    { id: "all", label: "All" },
    { id: "active", label: "Active" },
    { id: "blocked", label: "Blocked" },
];

const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString();
};

const avatarFallback = (user) => {
    if (!user?.name) return "U";
    return user.name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
};

export default function Users() {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    const [walletHistory, setWalletHistory] = useState([]);
    const [watchHistory, setWatchHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Multi-delete state
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setSelectedUser(null);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            setError("");
            try {
                const { data } = await API.get("/admin/users", {
                    params: { page, limit, search },
                });

                if (!data?.success) {
                    throw new Error(data?.message || "Failed to load users");
                }

                setUsers(Array.isArray(data.users) ? data.users : []);
                setTotal(data.total || 0);
            } catch (err) {
                setError(err?.response?.data?.message || err.message || "Failed to load users");
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [page, limit, search]);

    const filteredUsers = useMemo(() => {
        return users.filter((u) => {
            if (statusFilter === "blocked") return !!u.isBlocked;
            if (statusFilter === "active") return !u.isBlocked;
            return true;
        });
    }, [users, statusFilter]);

    const totalPages = Math.max(Math.ceil(total / limit), 1);

    useEffect(() => {
        const fetchUserHistories = async () => {
            if (!selectedUser?._id) {
                setWalletHistory([]);
                setWatchHistory([]);
                return;
            }

            try {
                setHistoryLoading(true);
                const [{ data: walletData }, { data: watchData }] = await Promise.all([
                    API.get(`/admin/users/${selectedUser._id}/wallet-history`, { params: { page: 1, limit: 8 } }),
                    API.get(`/admin/users/${selectedUser._id}/watch-history`, { params: { page: 1, limit: 8 } }),
                ]);

                setWalletHistory(Array.isArray(walletData?.transactions) ? walletData.transactions : []);
                setWatchHistory(Array.isArray(watchData?.sessions) ? watchData.sessions : []);
            } catch (err) {
                setError(err?.response?.data?.message || err.message || "Failed to load user histories");
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchUserHistories();
    }, [selectedUser?._id]);

    const onBanToggle = async (user) => {
        try {
            setActionLoading(true);
            const path = user.isBlocked ? "unban" : "ban";
            await API.patch(`/admin/users/${user._id}/${path}`);
            setUsers((prev) =>
                prev.map((u) => (u._id === user._id ? { ...u, isBlocked: !user.isBlocked } : u))
            );
            if (selectedUser?._id === user._id) {
                setSelectedUser((prev) => prev ? { ...prev, isBlocked: !user.isBlocked } : null);
            }
        } catch (err) {
            setError(err?.response?.data?.message || "Action failed");
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggleSelectAll = () => {
        if (selectedIds.length === filteredUsers.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredUsers.map((u) => u._id));
        }
    };

    const handleToggleSelectRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleBatchDelete = async () => {
        try {
            setIsBatchDeleting(true);
            const { data } = await API.post("/admin/batch-delete", {
                entity: "users",
                ids: selectedIds,
            });
            if (data?.success) {
                setSelectedIds([]);
                setUsers((prev) => prev.filter((u) => !selectedIds.includes(u._id)));
                if (selectedUser && selectedIds.includes(selectedUser._id)) {
                    setSelectedUser(null);
                }
            }
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Batch delete failed");
        } finally {
            setIsBatchDeleting(false);
        }
    };

    const onDelete = async (user) => {
        if (!window.confirm("Delete this user? This cannot be undone.")) return;
        try {
            setActionLoading(true);
            await API.delete(`/admin/users/${user._id}`);
            setUsers((prev) => prev.filter((u) => u._id !== user._id));
            setSelectedIds((prev) => prev.filter((i) => i !== user._id));
            if (selectedUser?._id === user._id) setSelectedUser(null);
            setTotal((prev) => Math.max(prev - 1, 0));
        } catch (err) {
            setError(err?.response?.data?.message || "Delete failed");
        } finally {
            setActionLoading(false);
        }
    };

    const renderStatus = (user) => {
        if (user.isBlocked) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400">
                    <ShieldOff className="w-3.5 h-3.5" />
                    Blocked
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400">
                <Shield className="w-3.5 h-3.5" />
                Active
            </span>
        );
    };

    return (
        <div className="flex flex-col xl:flex-row gap-5 lg:gap-6 pb-12 lg:pl-5 xl:pl-7 2xl:pl-0">
            <div className={`flex-1 flex flex-col min-w-0 ${selectedUser ? "hidden xl:flex" : "flex"}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 lg:gap-4 mb-4 lg:mb-6">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100">User Management</h1>
                        <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 mt-0.5 lg:mt-1">View, block/unblock, and delete users</p>
                    </div>
                    <div className="flex items-center gap-2 lg:gap-3">
                        <button
                            onClick={() => setPage(1)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs lg:text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-1.5 lg:gap-2 mb-4 lg:mb-6 overflow-x-auto pb-1 lg:pb-2 scrollbar-hide">
                    {statusTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setStatusFilter(tab.id)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs lg:text-sm font-medium whitespace-nowrap transition-all flex-shrink-0
                                ${statusFilter === tab.id
                                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25"
                                    : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-300"}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, phone, username"
                            value={searchInput}
                            onChange={(e) => { setPage(1); setSearchInput(e.target.value); }}
                            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 shadow-sm"
                        />
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200/90 dark:border-gray-700/80 shadow-sm overflow-hidden flex flex-col">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 px-6 py-3.5 text-sm flex items-center gap-2 border-b border-red-100 dark:border-red-500/20">
                            <AlertTriangle className="w-4 h-4" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[700px]">
                            <thead className="bg-gray-50 dark:bg-slate-800/95 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                                <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 w-14 xl:w-16 text-center align-middle">
                                        <div className="flex items-center justify-center">
                                            <input
                                                type="checkbox"
                                                checked={filteredUsers.length > 0 && selectedIds.length === filteredUsers.length}
                                                onChange={handleToggleSelectAll}
                                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                            />
                                        </div>
                                    </th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-left font-semibold">User</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-left font-semibold">Phone</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-left font-semibold">Coins</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-left font-semibold">Rating</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-left font-semibold hidden md:table-cell">Joined</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-left font-semibold hidden md:table-cell">Status</th>
                                    <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-center font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
                                {loading ? (
                                    <tr>
                                        <td colSpan={8} className="py-14 text-center text-gray-500 dark:text-gray-300">
                                            <div className="flex items-center justify-center gap-2.5">
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                Loading users...
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="py-14 text-center text-gray-500 dark:text-gray-300">
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map((user) => {
                                        const isSelected = selectedIds.includes(user._id);
                                        return (
                                        <tr
                                            key={user._id}
                                            onClick={() => setSelectedUser(user)}
                                            className={`transition-colors cursor-pointer ${isSelected ? "bg-blue-50/80 dark:bg-blue-500/10" : selectedUser?._id === user._id ? "bg-blue-50 dark:bg-blue-500/5" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}
                                        >
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleToggleSelectRow(user._id)}
                                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5">
                                                <div className="flex items-center gap-3.5">
                                                    <div className="relative flex-shrink-0">
                                                        {user.mainImage ? (
                                                            <img
                                                                src={user.mainImage}
                                                                alt={user.name}
                                                                className="w-10 h-10 rounded-full object-cover border border-white/40 shadow-sm"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm">
                                                                {avatarFallback(user)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{user.name || "Unnamed"}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">@{user.username || "unknown"}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-sm text-gray-700 dark:text-gray-200 font-medium whitespace-nowrap">{user.phone || "-"}</td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-sm text-cyan-600 dark:text-cyan-300 font-semibold">{user?.wallet?.balanceCoins ?? 0}</td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-sm whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
                                                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 flex-shrink-0" />
                                                    {user.ratingCount > 0 ? Number(user.rating).toFixed(1) : "0.0"}
                                                    <span className="text-[10px] text-gray-400 font-normal">({user.ratingCount || 0})</span>
                                                </span>
                                            </td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 text-sm text-gray-600 dark:text-gray-300 hidden md:table-cell whitespace-nowrap">{formatDate(user.createdAt)}</td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5 hidden md:table-cell whitespace-nowrap">{renderStatus(user)}</td>
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 2xl:py-4.5">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onBanToggle(user); }}
                                                        disabled={actionLoading}
                                                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors border
                                                            ${user.isBlocked
                                                                ? "border-green-200 text-green-700 bg-green-50 hover:bg-green-100 dark:border-green-500/40 dark:text-green-300 dark:bg-green-500/10"
                                                                : "border-red-200 text-red-700 bg-red-50 hover:bg-red-100 dark:border-red-500/40 dark:text-red-300 dark:bg-red-500/10"}`}
                                                    >
                                                        {user.isBlocked ? "Unblock" : "Block"}
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onDelete(user); }}
                                                        disabled={actionLoading}
                                                        className="p-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                                        title="Delete User"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-slate-800">
                        <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">
                            Page {page} of {totalPages} — {total} users
                        </p>
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                disabled={page === 1 || loading}
                                className="p-1.5 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                disabled={page >= totalPages || loading}
                                className="p-1.5 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Multi-Delete Bar */}
            <BatchActionBar
                selectedCount={selectedIds.length}
                totalCount={total}
                onClearSelection={() => setSelectedIds([])}
                onSelectAll={handleToggleSelectAll}
                onDeleteSelected={handleBatchDelete}
                isDeleting={isBatchDeleting}
                entityName="users"
            />

            {selectedUser && (
                <div className="xl:w-80 2xl:w-96 bg-white dark:bg-slate-800 rounded-xl lg:rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-slideInRight flex flex-col">
                    <div className="relative h-28 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 flex items-end px-4 pb-4">
                        {/* Close Button */}
                        <button
                            type="button"
                            onClick={() => setSelectedUser(null)}
                            aria-label="Close user details"
                            title="Close (Esc)"
                            className="absolute top-3 right-3 p-1.5 rounded-full bg-black/25 hover:bg-black/45 text-white backdrop-blur-md transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer shadow-sm group"
                        >
                            <X className="w-4 h-4 transition-transform group-hover:rotate-90 duration-200" />
                        </button>

                        <div className="relative -mb-8">
                            {selectedUser.mainImage || (selectedUser.mainImage && selectedUser.mainImage[0]) ? (
                                <img
                                    src={selectedUser.mainImage || selectedUser.mainImage[0]}
                                    alt={selectedUser.name}
                                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                                />
                            ) : (
                                <div className="w-20 h-20 rounded-full bg-white/20 border-4 border-white backdrop-blur-sm flex items-center justify-center text-white text-xl font-bold shadow-lg">
                                    {avatarFallback(selectedUser)}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto pt-10 px-4 pb-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{selectedUser.name || "Unnamed"}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">@{selectedUser.username || "unknown"}</p>
                            </div>
                            <div className="px-2 py-1 rounded-full text-[10px] font-semibold bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-200 flex items-center gap-1">
                                <UsersIcon className="w-3.5 h-3.5" />
                                User
                            </div>
                        </div>

                        <div className="mt-4 space-y-3 text-sm text-gray-700 dark:text-gray-200">
                            <div className="flex items-center gap-2">
                                <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
                                <span className="font-semibold text-amber-600 dark:text-amber-400">
                                    Rating: {selectedUser.ratingCount > 0 ? Number(selectedUser.rating).toFixed(1) : "0.0"} / 5.0
                                </span>
                                <span className="text-xs text-gray-400">({selectedUser.ratingCount || 0} reviews)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-gray-400" />
                                <span>{selectedUser.phone || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span>Joined {formatDate(selectedUser.createdAt)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-medium">
                                <Headset className="w-4 h-4 text-amber-500" />
                                <span>Support Tickets: {selectedUser.supportTicketsCount || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {renderStatus(selectedUser)}
                            </div>
                            <div>
                                <p className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Bio</p>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{selectedUser.bio || "No bio"}</p>
                            </div>

                            <div className="rounded-xl border border-gray-100 dark:border-slate-700 p-3 bg-gray-50 dark:bg-slate-900/50">
                                <p className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400 mb-2 inline-flex items-center gap-1">
                                    <Wallet className="w-3.5 h-3.5" /> Wallet Summary
                                </p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="rounded-lg bg-white dark:bg-slate-800 p-2">
                                        <p className="text-gray-500">Balance</p>
                                        <p className="font-semibold text-cyan-600 dark:text-cyan-300">{selectedUser?.wallet?.balanceCoins ?? 0}</p>
                                    </div>
                                    <div className="rounded-lg bg-white dark:bg-slate-800 p-2">
                                        <p className="text-gray-500">Purchased</p>
                                        <p className="font-semibold">{selectedUser?.wallet?.lifetimePurchased ?? 0}</p>
                                    </div>
                                    <div className="rounded-lg bg-white dark:bg-slate-800 p-2">
                                        <p className="text-gray-500">Spent</p>
                                        <p className="font-semibold">{selectedUser?.wallet?.lifetimeSpent ?? 0}</p>
                                    </div>
                                    <div className="rounded-lg bg-white dark:bg-slate-800 p-2">
                                        <p className="text-gray-500">Earned</p>
                                        <p className="font-semibold">{selectedUser?.wallet?.lifetimeEarned ?? 0}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-xl border border-gray-100 dark:border-slate-700 p-3 bg-gray-50 dark:bg-slate-900/50">
                                <p className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400 mb-2 inline-flex items-center gap-1">
                                    <Coins className="w-3.5 h-3.5" /> Coin Transactions
                                </p>
                                {historyLoading ? (
                                    <div className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...</div>
                                ) : walletHistory.length === 0 ? (
                                    <p className="text-xs text-gray-500">No transactions</p>
                                ) : (
                                    <div className="space-y-2 max-h-36 overflow-auto">
                                        {walletHistory.map((tx) => (
                                            <div key={tx._id} className="text-xs rounded-lg bg-white dark:bg-slate-800 p-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-semibold">{tx.source || tx.type}</span>
                                                    <span className="text-cyan-600 dark:text-cyan-300">{tx.amount}c</span>
                                                </div>
                                                <p className="text-gray-500">{tx.fromUser?.name || "System"} -&gt; {tx.toUser?.name || "N/A"}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="rounded-xl border border-gray-100 dark:border-slate-700 p-3 bg-gray-50 dark:bg-slate-900/50">
                                <p className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400 mb-2 inline-flex items-center gap-1">
                                    <Activity className="w-3.5 h-3.5" /> Watch History
                                </p>
                                {historyLoading ? (
                                    <div className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...</div>
                                ) : watchHistory.length === 0 ? (
                                    <p className="text-xs text-gray-500">No session history</p>
                                ) : (
                                    <div className="space-y-2 max-h-36 overflow-auto">
                                        {watchHistory.map((session) => (
                                            <div key={session._id} className="text-xs rounded-lg bg-white dark:bg-slate-800 p-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-semibold uppercase">{session.type}</span>
                                                    <span>{session.status}</span>
                                                </div>
                                                <p className="text-gray-500">{session.callerId?.name || "Caller"} with {session.receiverId?.name || "Receiver"}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-4 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => onBanToggle(selectedUser)}
                                    disabled={actionLoading}
                                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors
                                        ${selectedUser.isBlocked
                                            ? "bg-green-500 text-white hover:bg-green-600"
                                            : "bg-red-500 text-white hover:bg-red-600"}`}
                                >
                                    {selectedUser.isBlocked ? "Unblock" : "Block"}
                                </button>
                                <button
                                    onClick={() => onDelete(selectedUser)}
                                    disabled={actionLoading}
                                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 cursor-pointer"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}



